import React from "react";
import { loadColor, LOAD_COLORS } from "../utils/reviewLoad";

// Matches TeamPage.js's admin badge exactly — a distinct designation color,
// not one of PrCard's priority colors or the load-color scale.
const ADMIN_BADGE_COLOR = "#a855f7";

function TeamMember({ member }) {
  // Deliberately `typeof`, not `|| 0`. The panel shipped for months rendering
  // a confident 0 for every member because the API never sent these fields at
  // all — `|| 0` is what made a missing number indistinguishable from a real
  // one. An absent count now reads as unknown and looks unknown.
  const known = typeof member.active_reviews === "number";
  const load = known ? member.active_reviews : null;

  // Free-tier members past the plan's cap are excluded from the routing pool
  // entirely (routing-engine.mjs's `u.plan_active = true`), so they will never
  // receive work no matter how idle they look. Colouring them light-load would
  // advertise capacity that cannot be used — the same false-confidence problem
  // as the `|| 0` above, one layer up.
  const routable = member.plan_active !== false;
  const color = routable ? loadColor(load) : LOAD_COLORS.unknown;

  const initials = (member.display_name || member.github_username || "??")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const chipLabel = known
    ? `${load} active review${load === 1 ? "" : "s"}`
    : "active reviews unknown";

  return (
    <div className="bg-rf-bg-secondary rounded-lg px-4 py-3.5 flex items-center gap-3.5">
      {/*
        Identity colour, not load colour. The chip already encodes load, and
        tinting the avatar too made every row carry the same signal twice.
        Matches TeamPage.js's MemberRow so the same person doesn't render
        differently on two pages.
      */}
      <div className="w-10 h-10 rounded-full bg-rf-accent-blue/15 text-rf-accent-blue flex items-center justify-center text-sm font-semibold shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        {/*
          ⚠️ `truncate` belongs on the NAME, not on this flex container. As a
          container it expands to overflow:hidden + text-overflow:ellipsis +
          nowrap, but text-overflow only applies to a block container's own
          inline content — the name here is an anonymous FLEX ITEM, so no
          ellipsis ever renders, the name refuses to shrink below its content
          width, and the Admin pill gets pushed out and clipped by the
          overflow:hidden. Intermittent, because it needs a long enough name.
          On the span it works, because overflow:hidden on a flex ITEM zeroes
          its automatic minimum size — which is what the username row below
          has quietly been relying on all along.
        */}
        <div className="text-sm font-semibold text-rf-text-primary flex items-center gap-2 min-w-0">
          <span className="truncate">{member.display_name}</span>
          {member.team_role === "admin" && (
            <span
              className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full shrink-0"
              style={{ color: ADMIN_BADGE_COLOR, background: `${ADMIN_BADGE_COLOR}18` }}
            >
              Admin
            </span>
          )}
        </div>
        <div className="text-[11px] text-rf-text-secondary font-mono flex items-center gap-2">
          <span className="truncate">@{member.github_username}</span>
          {!routable && (
            <span
              className="text-[10px] font-sans font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
              style={{ color: LOAD_COLORS.unknown, background: `${LOAD_COLORS.unknown}22` }}
              title="Outside your plan's member limit, so Anthurium won't route new reviews here."
            >
              Not routed
            </span>
          )}
        </div>
      </div>
      {/*
        min-w + tabular-nums are what make this a scannable column rather than
        a ragged edge: without both, a two-digit count shifts the chip and the
        alignment that justifies right-aligning at all is lost.

        sr-only rather than aria-label — aria-label on a generic div is not
        reliably exposed by every screen reader. The visible glyph is
        aria-hidden and the sr-only span carries the whole phrase, so it is
        announced once as "3 active reviews" rather than twice as "3, 3 active
        reviews" — and the unknown state announces as words instead of an em
        dash, which screen readers pronounce inconsistently or skip entirely.
      */}
      <div
        className="shrink-0 min-w-[2.75rem] text-center rounded-md px-2 py-1 text-base font-bold font-mono tabular-nums"
        style={{ color, background: `${color}18` }}
        data-testid="active-reviews"
        title={known ? undefined : "No data for this member yet."}
      >
        <span aria-hidden="true">{known ? load : "—"}</span>
        <span className="sr-only">{chipLabel}</span>
      </div>
    </div>
  );
}

/**
 * Team Capacity — one number per member: how many PRs are currently assigned
 * to them and not yet merged or closed.
 *
 * It used to render three numbers (Pending / Active / Done) and all three were
 * always 0, because GET /api/teams/me produced none of them. Only "Active" is
 * derivable from truthful data: nothing in the schema records *who* reviewed a
 * PR, so a per-member "Done" count could only be inferred from a merge — the
 * exact overclaim gotcha #26 avoided when it named the status 'resolved'
 * rather than 'completed'.
 *
 * `loaded` is required and is not cosmetic. Dashboard swallows getMyTeam()'s
 * error (console.log in its inner catch), so a failed fetch arrives here as an
 * empty members array that is indistinguishable from a genuinely empty team —
 * and this panel would then print "No team members found", a confident claim
 * about a team we failed to load. That is gotchas #44/#60, which have now
 * recurred three times in four PRs. Unknown state must render as unknown.
 */
/**
 * `teamless` is a THIRD state, not a reuse of the second one. `loaded=false`
 * means "we tried to fetch the team and failed", and prints "Team
 * unavailable" — which would report an error to someone who simply has no
 * team, where nothing is wrong at all. Three distinct states, three distinct
 * sentences: no team / fetch failed / loaded but empty.
 */
function TeamPanel({ members = [], loaded = false, teamless = false }) {
  // Sorted here rather than in SQL on purpose. The same `members` array also
  // fills Dashboard's reviewer <select> and TeamPage's member list, and
  // ordering it by load server-side would make that dropdown reshuffle itself
  // whenever review load changed. The API sorts alphabetically; the load view
  // is this panel's own concern. Unknown counts sort last (-1).
  const sorted = [...members].sort(
    (a, b) =>
      (typeof b.active_reviews === "number" ? b.active_reviews : -1) -
      (typeof a.active_reviews === "number" ? a.active_reviews : -1)
  );

  // The column caption is rendered only when there are actually rows under
  // it. In the unavailable and empty states it would be labelling a column
  // that isn't there — a small version of the same false claim the `loaded`
  // prop exists to prevent.
  const hasRows = !teamless && loaded && sorted.length > 0;

  return (
    <div data-testid="team-capacity-panel">
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h2 className="text-base font-semibold text-rf-text-primary">
          Team Capacity
        </h2>
        {hasRows && (
          <span className="text-[11px] uppercase tracking-wider text-rf-text-secondary shrink-0">
            Active reviews
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2.5">
        {teamless ? (
          <div className="text-center py-8 text-rf-text-secondary text-sm">
            No team yet
          </div>
        ) : !loaded ? (
          <div className="text-center py-8 text-rf-text-secondary text-sm">
            Team unavailable
          </div>
        ) : sorted.length > 0 ? (
          sorted.map((m) => <TeamMember key={m.github_username} member={m} />)
        ) : (
          <div className="text-center py-8 text-rf-text-secondary text-sm">
            No team members found
          </div>
        )}
      </div>
    </div>
  );
}

export default TeamPanel;
